import React from "react";
import moment from "moment";
import { numberWithCommas, switchDotsAndCommas } from "../../util/utilFunction";

const formatAngka = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return switchDotsAndCommas(numberWithCommas(value));
};

function getWeekOfMonth(dateStr) {
  const date = moment(dateStr, "YYYY-MM-DD");
  const startOfMonth = date.clone().startOf("month");
  const firstWeekStart = startOfMonth.clone().startOf("isoWeek");

  const diffDays = date.diff(firstWeekStart, "days");
  const weekNumber = Math.floor(diffDays / 7) + 1;

  return weekNumber > 4 ? 4 : weekNumber;
}

function hitungBahanPerWeek(jadwalKirim = [], berat = 0, runner = 0) {
  const totalPerWeek = { 1: 0, 2: 0, 3: 0, 4: 0 };

  jadwalKirim.forEach((jadwal) => {
    if (!jadwal?.date || !jadwal?.jumlah) return;
    const week = getWeekOfMonth(jadwal.date);
    totalPerWeek[week] += jadwal.jumlah;
  });

  return {
    week1: (totalPerWeek[1] * (berat + runner)) / 1000,
    week2: (totalPerWeek[2] * (berat + runner)) / 1000,
    week3: (totalPerWeek[3] * (berat + runner)) / 1000,
    week4: (totalPerWeek[4] * (berat + runner)) / 1000,
  };
}

function hitungPewarnaPerWeek(
  jadwalKirim = [],
  berat = 0,
  runner = 0,
  komposisiMaba = 0
) {
  const totalPerWeek = { 1: 0, 2: 0, 3: 0, 4: 0 };

  jadwalKirim.forEach((jadwal) => {
    if (!jadwal?.date || !jadwal?.jumlah) return;
    const week = getWeekOfMonth(jadwal.date);
    totalPerWeek[week] += jadwal.jumlah;
  });

  const faktor = (berat + runner) * ((komposisiMaba ?? 0) / 100);

  return {
    week1: (totalPerWeek[1] * faktor) / 1000,
    week2: (totalPerWeek[2] * faktor) / 1000,
    week3: (totalPerWeek[3] * faktor) / 1000,
    week4: (totalPerWeek[4] * faktor) / 1000,
  };
}

function getPemakaianPerWeek(pemakaian) {
  const p = pemakaian || {};
  return {
    week1: (p.week1 ?? 0) / 1000,
    week2: (p.week2 ?? 0) / 1000,
    week3: (p.week3 ?? 0) / 1000,
    week4: (p.week4 ?? 0) / 1000,
  };
}

function hitungTotal(items) {
  const seenMaterials = new Set();

  return items.reduce(
    (acc, item) => {
      const berat = item.dataBarangJadi?.berat ?? 0;
      const runner = item.dataBarangJadi?.runner ?? 0;
      const jumlah = item.jumlah ?? 0;
      const totalBahan = (jumlah * (berat + runner)) / 1000;

      const { week1, week2, week3, week4 } = hitungBahanPerWeek(
        item.jadwalKirim,
        berat,
        runner
      );

      let pw1 = 0;
      let pw2 = 0;
      let pw3 = 0;
      let pw4 = 0;
      let stockAwal = 0;

      const materialKey = item.dataBarangJadi?.material;

      if (materialKey && !seenMaterials.has(materialKey)) {
        seenMaterials.add(materialKey);

        ({
          week1: pw1,
          week2: pw2,
          week3: pw3,
          week4: pw4,
        } = getPemakaianPerWeek(item.dataBarangJadi?.pemakaianMaterial));

        stockAwal = item.dataBarangJadi?.stockAwalMaterial ?? 0;
      }

      acc.pemakaian += totalBahan;
      acc.week1 += week1 - stockAwal;
      acc.week2 += week2;
      acc.week3 += week3;
      acc.week4 += week4;

      acc.pWeek1 += pw1;
      acc.pWeek2 += pw2;
      acc.pWeek3 += pw3;
      acc.pWeek4 += pw4;

      return acc;
    },
    {
      pemakaian: 0,
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      pWeek1: 0,
      pWeek2: 0,
      pWeek3: 0,
      pWeek4: 0,
    }
  );
}

function hitungTotalPewarna(items) {
  const seenPewarna = new Set();

  return items.reduce(
    (acc, item) => {
      const berat = item.dataBarangJadi?.berat ?? 0;
      const runner = item.dataBarangJadi?.runner ?? 0;
      const komposisiMaba = item.dataBarangJadi?.komposisiMaba ?? 0;
      const jumlah = item.jumlah ?? 0;
      const faktor = (berat + runner) * (komposisiMaba / 100);
      const totalBahan = (jumlah * faktor) / 1000;

      const { week1, week2, week3, week4 } = hitungPewarnaPerWeek(
        item.jadwalKirim,
        berat,
        runner,
        komposisiMaba
      );

      let pw1 = 0;
      let pw2 = 0;
      let pw3 = 0;
      let pw4 = 0;
      let stockAwal = 0;

      const pewarnaKey = item.dataBarangJadi?.pewarna;

      if (pewarnaKey && !seenPewarna.has(pewarnaKey)) {
        seenPewarna.add(pewarnaKey);

        ({
          week1: pw1,
          week2: pw2,
          week3: pw3,
          week4: pw4,
        } = getPemakaianPerWeek(item.dataBarangJadi?.pemakaianPewarna));

        // Same as above: subtract only once per unique pewarna.
        stockAwal = item.dataBarangJadi?.stockAwalPewarna ?? 0;
      }

      acc.pemakaian += totalBahan;
      acc.week1 += week1 - stockAwal;
      acc.week2 += week2;
      acc.week3 += week3;
      acc.week4 += week4;

      acc.pWeek1 += pw1;
      acc.pWeek2 += pw2;
      acc.pWeek3 += pw3;
      acc.pWeek4 += pw4;

      return acc;
    },
    {
      pemakaian: 0,
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      pWeek1: 0,
      pWeek2: 0,
      pWeek3: 0,
      pWeek4: 0,
    }
  );
}

const thStyle = {
  border: "1px solid #d0d0d0",
  padding: "8px 10px",
  background: "#f2f2f2",
  fontSize: "13px",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const thForecastGroupStyle = {
  ...thStyle,
  background: "#bcdcf7",
  color: "#0b4a80",
  fontWeight: 700,
};

const thForecastStyle = {
  ...thStyle,
  background: "#e3f0fb",
};

const thUsageGroupStyle = {
  ...thStyle,
  background: "#ffd9a8",
  color: "#7a4200",
  fontWeight: 700,
};

const thUsageStyle = {
  ...thStyle,
  background: "#fff1de",
};

const tdStyle = {
  border: "1px solid #e0e0e0",
  padding: "8px 10px",
  fontSize: "13px",
  textAlign: "right",
  fontWeight: "bold",
};

const tdForecastStyle = {
  ...tdStyle,
  background: "#eef6fd",
};

const tdUsageStyle = {
  ...tdStyle,
  background: "#fff6ea",
};

const tdSelisihPositifStyle = {
  ...tdStyle,
  background: "#e6f7ea",
  color: "#0f6b2c",
};

const tdSelisihNegatifStyle = {
  ...tdStyle,
  background: "#fde8e8",
  color: "#a3231f",
};

const MrpTable = ({ title, rows }) => (
  <div style={{ overflowX: "auto", marginTop: "12px" }}>
    <h3 style={{ marginBottom: "8px" }}>{title}</h3>
    <table
      style={{
        borderCollapse: "collapse",
        width: "100%",
        minWidth: "1450px",
      }}
    >
      <thead>
        <tr>
          <th style={thStyle} rowSpan={2}>
            Nama Bahan
          </th>
          <th style={thStyle} rowSpan={2}>
            Pemakaian <br />
            (Kg)
          </th>
          <th style={thForecastGroupStyle} colSpan={4}>
            Forecast (Kg)
          </th>
          <th style={thUsageGroupStyle} colSpan={4}>
            Penggunaan Aktual (Kg)
          </th>
          <th style={thStyle} rowSpan={2}>
            Selisih (Kg)
          </th>
        </tr>
        <tr>
          <th style={thForecastStyle}>W 1</th>
          <th style={thForecastStyle}>W 2</th>
          <th style={thForecastStyle}>W 3</th>
          <th style={thForecastStyle}>W 4</th>
          <th style={thUsageStyle}>W 1</th>
          <th style={thUsageStyle}>W 2</th>
          <th style={thUsageStyle}>W 3</th>
          <th style={thUsageStyle}>W 4</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td style={{ ...tdStyle, textAlign: "left" }}>{row.label}</td>
            <td style={tdStyle}>{formatAngka(row.pemakaian)}</td>
            <td style={tdForecastStyle}>{formatAngka(row.week1)}</td>
            <td style={tdForecastStyle}>{formatAngka(row.week2)}</td>
            <td style={tdForecastStyle}>{formatAngka(row.week3)}</td>
            <td style={tdForecastStyle}>{formatAngka(row.week4)}</td>
            <td style={tdUsageStyle}>{formatAngka(row.pWeek1)}</td>
            <td style={tdUsageStyle}>{formatAngka(row.pWeek2)}</td>
            <td style={tdUsageStyle}>{formatAngka(row.pWeek3)}</td>
            <td style={tdUsageStyle}>{formatAngka(row.pWeek4)}</td>
            <td
              style={
                row.selisih < 0 ? tdSelisihNegatifStyle : tdSelisihPositifStyle
              }
            >
              {formatAngka(row.selisih)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MrpSummaryTable = ({ data, filterBahan, sampleBahan = [] }) => {
  if (!data || data.length === 0) return null;

  let rows = [];

  if (filterBahan === "Pilih Bahan") {
    rows = sampleBahan.map((bahan) => {
      const items = data.filter(
        (item) => item.dataBarangJadi?.bahanBaku === bahan.value
      );
      const totals = hitungTotal(items);
      const actualTotal =
        totals.pWeek1 + totals.pWeek2 + totals.pWeek3 + totals.pWeek4;
      return {
        label: bahan.label,
        ...totals,
        selisih: totals.pemakaian - actualTotal,
      };
    });
  } else {
    const items = data.filter(
      (item) => item.dataBarangJadi?.bahanBaku === filterBahan
    );
    const label =
      sampleBahan.find((b) => b.value === filterBahan)?.label || filterBahan;
    const totals = hitungTotal(items);
    const actualTotal =
      totals.pWeek1 + totals.pWeek2 + totals.pWeek3 + totals.pWeek4;
    rows = [{ label, ...totals, selisih: totals.pemakaian - actualTotal }];
  }

  const uniquePewarnaKode = [
    ...new Set(
      data.map((item) => item.dataBarangJadi?.pewarna).filter((kode) => !!kode)
    ),
  ];

  const pewarnaRows = uniquePewarnaKode.map((kode) => {
    const items = data.filter((item) => item.dataBarangJadi?.pewarna === kode);
    const totals = hitungTotalPewarna(items);
    const actualTotal =
      totals.pWeek1 + totals.pWeek2 + totals.pWeek3 + totals.pWeek4;
    return {
      label: kode,
      ...totals,
      selisih: totals.pemakaian - actualTotal,
    };
  });

  return (
    <>
      <MrpTable title="Bahan Baku" rows={rows} />
      {pewarnaRows.length > 0 && (
        <MrpTable title="Pewarna" rows={pewarnaRows} />
      )}
    </>
  );
};

export default MrpSummaryTable;
